import { useState, useMemo, useEffect } from 'react'
import ProviderSectionContainer, {
  PROVIDER_SECTION_IDS,
} from 'app/ProviderDetails/ProviderSectionContainer'
import useBreakpoint from 'hooks/useBreakPoint'
import { usePaginatedTestimonials } from 'hooks/provider/usePaginatedTestimonials'
import {
  Choose,
  If,
  Otherwise,
  When,
} from '@/utility_components/ControlStatements'
import {
  TESTIMONIALS_DISCLAIMER_TEXT,
  TESTIMONIALS_INITIAL_COUNT_DESKTOP,
  TESTIMONIALS_INITIAL_COUNT_MOBILE,
} from 'app/ProviderDetails/constants'
import { cn } from '@/lib/utils'
import TestimonialCard from './TestimonialCard'
import TestimonialsBottomSheet from './TestimonialsBottomSheet'
import TestimonialsDesktopModal from './TestimonialsDesktopModal'
import styles from './index.module.scss'

type TestimonialsSectionProps = {
  providerDetails: Provider
  testimonialsModalOpen?: boolean
  onCloseTestimonialsModal?: () => void
  onOpenTestimonialsModal?: () => void
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  providerDetails,
  testimonialsModalOpen = false,
  onCloseTestimonialsModal,
  onOpenTestimonialsModal,
}) => {
  const [showAll, setShowAll] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [scrollToTestimonialId, setScrollToTestimonialId] = useState<
    string | null
  >(null)
  const [pendingScrollIndex, setPendingScrollIndex] = useState<number | null>(
    null
  )
  const { isMobileScreen } = useBreakpoint()
  const { firstName, testimonials } = providerDetails

  const sortedItems = useMemo(
    () =>
      (testimonials?.items ?? []).filter(
        (t) => (t.reviewBody ?? '').trim().length > 0
      ),
    [testimonials?.items]
  )

  const {
    items: paginatedItems,
    loadMore,
    hasMore: hasMorePaginated,
    isLoading,
  } = usePaginatedTestimonials({
    providerId: providerDetails?.id ?? '',
    initialItems: sortedItems,
  })

  const filteredPaginatedItems = useMemo(
    () => paginatedItems.filter((t) => (t.reviewBody ?? '').trim().length > 0),
    [paginatedItems]
  )

  const initialCount = isMobileScreen
    ? TESTIMONIALS_INITIAL_COUNT_MOBILE
    : TESTIMONIALS_INITIAL_COUNT_DESKTOP

  const displayItems = showAll
    ? sortedItems
    : sortedItems.slice(0, initialCount)
  const canOpenExpandedView = isMobileScreen || Boolean(onOpenTestimonialsModal)
  const hasInlineOverflow = sortedItems.length > initialCount
  const totalReviewCount = testimonials?.summary?.reviewCount ?? 0
  const hasMore = canOpenExpandedView
    ? hasInlineOverflow || totalReviewCount > initialCount
    : hasInlineOverflow

  const sheetVisible =
    (isMobileScreen && sheetOpen) || (!isMobileScreen && testimonialsModalOpen)

  useEffect(() => {
    setSheetOpen(testimonialsModalOpen && isMobileScreen)
  }, [testimonialsModalOpen, isMobileScreen])

  useEffect(() => {
    if (pendingScrollIndex === null) {
      return
    }

    const item = filteredPaginatedItems[pendingScrollIndex]

    if (item) {
      setScrollToTestimonialId(item.id)
      setPendingScrollIndex(null)
    }
  }, [pendingScrollIndex, filteredPaginatedItems])

  const handleShowMoreClick = () => {
    const firstHiddenItem = sortedItems[initialCount] ?? null
    const scrollTarget = firstHiddenItem?.id ?? null

    if (isMobileScreen) {
      if (scrollTarget) {
        setScrollToTestimonialId(scrollTarget)
      } else {
        setPendingScrollIndex(initialCount)
      }

      setSheetOpen(true)
    } else if (onOpenTestimonialsModal) {
      if (scrollTarget) {
        setScrollToTestimonialId(scrollTarget)
      } else {
        setPendingScrollIndex(initialCount)
      }

      onOpenTestimonialsModal()
    } else {
      setShowAll(!showAll)
    }
  }

  const handleReadMoreClick = (itemId: string) => {
    if (isMobileScreen) {
      setScrollToTestimonialId(itemId)
      setSheetOpen(true)
    } else if (onOpenTestimonialsModal) {
      setScrollToTestimonialId(itemId)
      onOpenTestimonialsModal()
    }
  }

  const handleSheetHide = () => {
    setSheetOpen(false)
    setScrollToTestimonialId(null)
    setPendingScrollIndex(null)
    onCloseTestimonialsModal?.()
  }

  return (
    <If condition={sortedItems.length > 0}>
      <>
        <ProviderSectionContainer
          id={PROVIDER_SECTION_IDS.TESTIMONIALS}
          heading="What patients say"
        >
          <div className={cn(styles.masonryGrid, '~mt-8')}>
            {displayItems.map((item) => (
              <div key={item.id} className={styles.masonryItem}>
                <TestimonialCard
                  item={item}
                  providerFirstName={firstName}
                  onReadMoreClick={
                    isMobileScreen || onOpenTestimonialsModal
                      ? handleReadMoreClick
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
          <p className="~font-gibson ~text-base ~font-normal ~text-black ~my-8">
            {TESTIMONIALS_DISCLAIMER_TEXT}
          </p>
          <If condition={hasMore}>
            <div className="~flex ~justify-center ~mt-8">
              <button
                type="button"
                className={cn(
                  '~inline-flex ~items-center ~justify-center ~gap-1 ~h-10 ~py-2 ~pr-5 ~pl-6',
                  '~font-gibson ~text-base ~font-medium ~leading-6 ~text-navy ~bg-white',
                  '~border ~border-grey ~rounded-full ~cursor-pointer ~no-underline',
                  '~outline-none ~focus-visible:~outline-2 ~focus-visible:~outline-sky ~focus-visible:~outline-offset-2'
                )}
                aria-label={
                  showAll ? 'Read fewer testimonials' : 'Read more testimonials'
                }
                onClick={handleShowMoreClick}
              >
                <span>
                  {isMobileScreen
                    ? 'Read more'
                    : showAll
                    ? 'Read less'
                    : 'Read more'}
                </span>
                <If condition={(!isMobileScreen && !showAll) || isMobileScreen}>
                  <img
                    src="/static/chevron-down.svg"
                    alt=""
                    className="~inline-block ~shrink-0 ~size-[18px]"
                    width={18}
                    height={18}
                    aria-hidden
                  />
                </If>
              </button>
            </div>
          </If>
        </ProviderSectionContainer>

        <Choose>
          <When condition={isMobileScreen}>
            <TestimonialsBottomSheet
              show={sheetVisible}
              onHide={handleSheetHide}
              items={filteredPaginatedItems}
              providerDetails={providerDetails}
              scrollToTestimonialId={scrollToTestimonialId}
              loadMore={loadMore}
              hasMore={hasMorePaginated}
              isLoading={isLoading}
            />
          </When>
          <Otherwise>
            <TestimonialsDesktopModal
              show={testimonialsModalOpen}
              onHide={handleSheetHide}
              items={filteredPaginatedItems}
              providerDetails={providerDetails}
              scrollToTestimonialId={scrollToTestimonialId}
              loadMore={loadMore}
              hasMore={hasMorePaginated}
              isLoading={isLoading}
            />
          </Otherwise>
        </Choose>
      </>
    </If>
  )
}

export default TestimonialsSection
